import { Component, OnDestroy, OnInit } from "@angular/core";
import { MatIconRegistry } from "@angular/material/icon";
import { DomSanitizer } from "@angular/platform-browser";
import { NavigationEnd, Router, RouterOutlet } from "@angular/router";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { Subscription } from "rxjs/internal/Subscription";
import { filter } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { AuthService } from "../../services/auth/auth.service";
import { IdleTimeoutService } from "../../services/idle-timeout.service";
import { VersionService } from "../../services/version/version.service";
import { WSUserService } from "../../services/web-socket/ws-user.service";
import { logger } from "../../util/Logger";
import { slideInAnimation } from "../../util/animations";
import {
  VersionUpdateActionEnum,
  VersionUpdateModalComponent,
} from "../../view/shared/version-update-modal/version-update-modal.component";

const className = "MainTemplateComponent";
declare let ga: Function;

@Component({
  animations: [slideInAnimation],
  selector: "app-main-template",
  templateUrl: "main-template.component.html",
  styleUrls: ["main-template.component.scss"],
  standalone: false,
})
export class MainTemplateComponent implements OnInit, OnDestroy {
  year: string = new Date().getFullYear().toString();

  borderColour = environment.borderColour;
  private modalSubscription!: Subscription;
  private isModalOpen = false; // Prevent multiple modals

  constructor(
    private matIconRegistry: MatIconRegistry,
    private domSanitizer: DomSanitizer,
    private idleTimeoutService: IdleTimeoutService,
    private authService: AuthService,
    private router: Router,
    private wsUserService: WSUserService,
    private versionService: VersionService,
    private modalService: NgbModal
  ) {
    this._registerIcons();
  }

  ngOnInit() {
    if (this.borderColour) {
      document.body.style.border = `3px solid ${this.borderColour}`;
      document.body.style.boxSizing = "border-box"; // Prevents layout issues
    }

    this.blurNumberInputOnWheel();
    this.setupIdleTimeoutNavigationWatcher();
    this.wsUserService.registerWSUser();

    // Run code on every route change
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      // This runs on every route change
      this.wsUserService.registerWSUser(); // Ensure WebSocket is registered on route change
    });

    // Subscribe to version update modal trigger
    this.modalSubscription = this.versionService.showUpdateModalSubject
      .pipe(
        filter((show) => show === true && !this.isModalOpen) // Only open if not already open
      )
      .subscribe(() => {
        this.openVersionUpdateModal();
      });
  }

  ngOnDestroy() {
    if (this.modalSubscription) {
      this.modalSubscription.unsubscribe();
    }
  }

  private openVersionUpdateModal() {
    this.isModalOpen = true;

    const modalRef = this.modalService.open(VersionUpdateModalComponent, {
      backdrop: "static", // Prevent closing by clicking backdrop
      keyboard: false, // Prevent closing with ESC key
      size: "md",
    });

    modalRef.result.then(
      (result) => {
        //Modal closed with refresh now
        this.isModalOpen = false;
        this.versionService.dismissUpdateModal();
      },
      (dismissed) => {
        //Modal closed with snooze or dismiss
        this.isModalOpen = false;
        if (dismissed === VersionUpdateActionEnum.Snooze) {
          this.versionService.snoozeVersionUpdate();
        } else {
          this.versionService.stopCheckingVersion();
        }
      }
    );
  }

  prepareRoute(outlet: RouterOutlet, data: string) {
    return outlet && outlet.activatedRouteData && outlet.activatedRouteData[data];
  }

  blurNumberInputOnWheel() {
    const signature = className + ".blurNumberInput: ";
    document.addEventListener("wheel", function () {
      if (
        document.activeElement &&
        document.activeElement["type"] &&
        document.activeElement["type"] === "number" &&
        document.activeElement["blur"]
      ) {
        logger.silly(signature + "Prevented mWheel in Number Input");
        document.activeElement["blur"]();
      }
    });
  }

  /**
   * Setup navigation watcher for idle timeout
   * This only watches navigation events to reset/stop the timer
   * The actual initialization happens in LoginComponent (after login)
   * and AuthGuardService (on page reload with valid JWT)
   */
  setupIdleTimeoutNavigationWatcher() {
    // Watch for navigation changes to reset/start idle timer
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      // Check if user is authenticated
      if (this.authService.isAuthenticated()) {
        // Reset idle timer on navigation
        this.idleTimeoutService.reset();
      } else {
        // Stop idle timer if not authenticated
        this.idleTimeoutService.stop();
      }
    });
  }

  private _registerIcons() {
    this.matIconRegistry.addSvgIcon(
      "analytics",
      this.domSanitizer.bypassSecurityTrustResourceUrl("/assets/images/icons/analytics.svg")
    );
    this.matIconRegistry.addSvgIcon(
      "arrow-left",
      this.domSanitizer.bypassSecurityTrustResourceUrl("/assets/images/icons/arrow-left.svg")
    );
    this.matIconRegistry.addSvgIcon(
      "arrow-right",
      this.domSanitizer.bypassSecurityTrustResourceUrl("/assets/images/icons/arrow-right.svg")
    );
    this.matIconRegistry.addSvgIcon(
      "chevron-down",
      this.domSanitizer.bypassSecurityTrustResourceUrl("/assets/images/icons/chevron-down.svg")
    );
    this.matIconRegistry.addSvgIcon(
      "circle-dotted",
      this.domSanitizer.bypassSecurityTrustResourceUrl("/assets/images/icons/circle-dotted.svg")
    );
    this.matIconRegistry.addSvgIcon(
      "download",
      this.domSanitizer.bypassSecurityTrustResourceUrl("/assets/images/icons/download.svg")
    );
    this.matIconRegistry.addSvgIcon(
      "funnel",
      this.domSanitizer.bypassSecurityTrustResourceUrl("/assets/images/icons/funnel.svg")
    );
    this.matIconRegistry.addSvgIcon(
      "home",
      this.domSanitizer.bypassSecurityTrustResourceUrl("/assets/images/icons/home.svg")
    );
    this.matIconRegistry.addSvgIcon(
      "login",
      this.domSanitizer.bypassSecurityTrustResourceUrl("/assets/images/icons/login.svg")
    );
    this.matIconRegistry.addSvgIcon(
      "logout",
      this.domSanitizer.bypassSecurityTrustResourceUrl("/assets/images/icons/logout.svg")
    );
    this.matIconRegistry.addSvgIcon(
      "phone",
      this.domSanitizer.bypassSecurityTrustResourceUrl("/assets/images/icons/phone.svg")
    );
    this.matIconRegistry.addSvgIcon(
      "rating-1",
      this.domSanitizer.bypassSecurityTrustResourceUrl("/assets/images/icons/rating-1.svg")
    );
    this.matIconRegistry.addSvgIcon(
      "rating-2",
      this.domSanitizer.bypassSecurityTrustResourceUrl("/assets/images/icons/rating-2.svg")
    );
    this.matIconRegistry.addSvgIcon(
      "rating-3",
      this.domSanitizer.bypassSecurityTrustResourceUrl("/assets/images/icons/rating-3.svg")
    );
    this.matIconRegistry.addSvgIcon(
      "rating-4",
      this.domSanitizer.bypassSecurityTrustResourceUrl("/assets/images/icons/rating-4.svg")
    );
    this.matIconRegistry.addSvgIcon(
      "rating-5",
      this.domSanitizer.bypassSecurityTrustResourceUrl("/assets/images/icons/rating-5.svg")
    );
    this.matIconRegistry.addSvgIcon(
      "refresh",
      this.domSanitizer.bypassSecurityTrustResourceUrl("/assets/images/icons/refresh.svg")
    );
    this.matIconRegistry.addSvgIcon(
      "restart",
      this.domSanitizer.bypassSecurityTrustResourceUrl("/assets/images/icons/restart.svg")
    );
    this.matIconRegistry.addSvgIcon(
      "search-advanced",
      this.domSanitizer.bypassSecurityTrustResourceUrl("/assets/images/icons/search-advanced.svg")
    );
    this.matIconRegistry.addSvgIcon(
      "search",
      this.domSanitizer.bypassSecurityTrustResourceUrl("/assets/images/icons/search.svg")
    );
    this.matIconRegistry.addSvgIcon(
      "settings",
      this.domSanitizer.bypassSecurityTrustResourceUrl("/assets/images/icons/settings.svg")
    );
    this.matIconRegistry.addSvgIcon(
      "survey-completed",
      this.domSanitizer.bypassSecurityTrustResourceUrl("/assets/images/icons/survey-completed.svg")
    );
    this.matIconRegistry.addSvgIcon(
      "survey-in-progress",
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        "/assets/images/icons/survey-in-progress.svg"
      )
    );
    this.matIconRegistry.addSvgIcon(
      "benchmark-icon",
      this.domSanitizer.bypassSecurityTrustResourceUrl("/assets/images/icons/benchmark-icon.svg")
    );
    this.matIconRegistry.addSvgIcon(
      "survey-products-icon",
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        "/assets/images/icons/survey-products-icon.svg"
      )
    );
    this.matIconRegistry.addSvgIcon(
      "user-icon",
      this.domSanitizer.bypassSecurityTrustResourceUrl("/assets/images/icons/user.svg")
    );
    this.matIconRegistry.addSvgIcon(
      "survey",
      this.domSanitizer.bypassSecurityTrustResourceUrl("/assets/images/icons/survey.svg")
    );
    this.matIconRegistry.addSvgIcon(
      "thumbsup",
      this.domSanitizer.bypassSecurityTrustResourceUrl("/assets/images/icons/thumbsup.svg")
    );
    this.matIconRegistry.addSvgIcon(
      "user",
      this.domSanitizer.bypassSecurityTrustResourceUrl("/assets/images/icons/user.svg")
    );
    this.matIconRegistry.addSvgIcon(
      "warning",
      this.domSanitizer.bypassSecurityTrustResourceUrl("/assets/images/icons/warning.svg")
    );
    this.matIconRegistry.addSvgIcon(
      "toggle-on",
      this.domSanitizer.bypassSecurityTrustResourceUrl("/assets/images/icons/toggle-on.svg")
    );
    this.matIconRegistry.addSvgIcon(
      "toggle-off",
      this.domSanitizer.bypassSecurityTrustResourceUrl("/assets/images/icons/toggle-off.svg")
    );
    this.matIconRegistry.addSvgIcon(
      "delete",
      this.domSanitizer.bypassSecurityTrustResourceUrl("/assets/images/icons/delete.svg")
    );
    this.matIconRegistry.addSvgIcon(
      "id-card",
      this.domSanitizer.bypassSecurityTrustResourceUrl("/assets/images/icons/id-card.svg")
    );
    this.matIconRegistry.addSvgIcon(
      "cancel",
      this.domSanitizer.bypassSecurityTrustResourceUrl("/assets/images/icons/cancel.svg")
    );
    this.matIconRegistry.addSvgIcon(
      "cancel-white",
      this.domSanitizer.bypassSecurityTrustResourceUrl("/assets/images/icons/cancel-white.svg")
    );
    this.matIconRegistry.addSvgIcon(
      "CFEP_Icons_Green_Completed_Surveys",
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        "/assets/images/icons/CFEP_Icons_Green_Completed_Surveys.svg"
      )
    );
    this.matIconRegistry.addSvgIcon(
      "CFEP_Icon_Paper_Navy",
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        "/assets/images/icons/CFEP_Icon_Paper_Navy.svg"
      )
    );
    this.matIconRegistry.addSvgIcon(
      "CFEP_Icon_Checkbox_Cyan",
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        "/assets/images/icons/CFEP_Icon_Checkbox_Cyan.svg"
      )
    );
    this.matIconRegistry.addSvgIcon(
      "email",
      this.domSanitizer.bypassSecurityTrustResourceUrl("/assets/images/icons/email.svg")
    );
    this.matIconRegistry.addSvgIcon(
      "CFEP_Icons_Slate_Surveys",
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        "/assets/images/icons/CFEP_Icons_Slate_Surveys.svg"
      )
    );
    this.matIconRegistry.addSvgIcon(
      "CFEP_Icon_Reporting_Slate",
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        "/assets/images/icons/CFEP_Icon_Reporting_Slate.svg"
      )
    );
    this.matIconRegistry.addSvgIcon(
      "CFEP_Icon_Impersonate_Shield_Slate",
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        "/assets/images/icons/CFEP_Icon_Impersonate_Shield_Slate.svg"
      )
    );
    this.matIconRegistry.addSvgIcon(
      "CFEP_Icon_Graph_with_line",
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        "/assets/images/icons/CFEP_Icon_Graph_with_line.svg"
      )
    );
  }
}
